import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { map, of, Subscription, switchMap, take } from 'rxjs';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { FocusMeDirective } from '../../../../shared/directives/focus-me.directive';
import { ScrollerDirective } from '../../../../shared/directives/scroller.directive';
import { CodeValue } from '../../../../shared/models/boc-available-value';
import { BusinessType } from '../../../../shared/models/business-type';
import { SoldToRelationshipGroup } from '../../../../shared/models/service';
import { LocationService } from '../../../../shared/services/location.service';
import {
	NAMESPACE_A2C_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { UserService } from '../../../../shared/services/user.service';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import {
	BocClient,
	DraftOrderItem,
	RDD_TYPE_DAY,
	RDD_TYPE_WEEK,
	RequestOrder,
	RequestProductData
} from '../../../models/eorder';
import { EorderWizardService } from '../../../services/eorder-wizard.service';
import { EorderApiService } from '../../../services/eorder.api.service';
import { ProductService } from '../../../services/product.api.service';

const HIDDEN_VARIABLES = new Set(['form', 'code', 'category', 'subCategory']);
const FIRST_ROW_VARIABLES = new Set(['steelGrade', 'thicknessValue', 'widthValue']);

@Component({
	selector: 'copo-eorder-order-card',
	templateUrl: './order-card.component.html',
	animations: pageTransitions,
	styleUrl: './order-card.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		CdkScrollable,
		LoadingSpinnerComponent,
		PageHeaderComponent,
		NgClass,
		MatButtonModule,
		FocusMeDirective,
		TranslateModule,
		MatExpansionModule,
		MatTooltipModule,
		MatIconModule
	],
})

export class OrderCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	readonly UNDERSCORE = UNDERSCORE;
	readonly FIRST_ROW = 'first_row';
	readonly VARIABLE_ROW = 'variable_row';
	readonly FILTERED_ROW = FIRST_ROW_VARIABLES;
	rddTypeWeek = RDD_TYPE_WEEK;
	rddTypeDay = RDD_TYPE_DAY;
	isError = false;
	isLoading = false;
	isOrderReady = false;
	isExpanded: boolean[] = [];
	order?: RequestOrder;
	orderItemList: DraftOrderItem[] = [];
	sub: Subscription | undefined;
	shipToList: BocClient[] = [];
	bocs?: Map<string, BocClient>;
	soldtoShiptoList?: BocClient[];
	tmodeList?: CodeValue[] = [];
	totalQuantity: number | undefined;
	userBocData?: SoldToRelationshipGroup[];
	serviceLevelList?: CodeValue[] = [];
	xcarbList?: CodeValue[] = [];
	currencyList?: CodeValue[] = [];
	busList?: BusinessType[] = [];
	templateValidityMap: Record<string, boolean> = {};
	readonly hasUserSubmitterRole: boolean;
	readonly orderSubscription: Subscription;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly eorderApiService: EorderApiService,
		private readonly eorderWizardService: EorderWizardService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly userService: UserService,
		private readonly locationService: LocationService,
		private readonly templateService: ProductService
	) {
		this.hasUserSubmitterRole = this.userService.user.isOfferSubmitter();
		const orderId = <number> this.route.snapshot.params['orderId'];
		this.orderSubscription = this.loadOrder(orderId);
	}

	loadOrder(orderId: number): Subscription {
		return this.eorderApiService.getEordrDraft(orderId).pipe(
			switchMap(order => this.loadBocsForOrder(order))
		).subscribe(
			{
				next: ({ order, results }) => {
					this.order = order;
					this.shipToList = results;
					this.tmodeList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'TMODE');
					this.serviceLevelList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'SERVICELEVEL');
					this.xcarbList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'XCARB');
					this.currencyList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'CURRENCY');
					const soldTo = order.soldToRef.replace(/^0+/, '');
					this.userService.getBusinessTypes(soldTo).then(businessTypes => {
						this.busList = businessTypes;
						this.busList.unshift({
							businessCode: '',
							businessDescription: 'None'
						});
					}).catch((error: unknown) => {
						console.error('Error fetching business types:', error);
					});
					this.orderItemList = <DraftOrderItem[]>order.listOfRequestOrderItem;
					this.isExpanded = this.orderItemList.map(() => true);
					this.totalQuantity = this.orderItemList.reduce(
						(sum, item) => {
							const num = Number(item.quantityAmount) || 0;
							return sum + Math.round(num * 100);
						},
						0
					) / 100;
					if (this.order.listOfRequestOrderItem?.length) {
						for (const item of this.order.listOfRequestOrderItem) {
							if (item.templateRef && /^\d+$/.test(item.templateRef)) {
								this.templateValidityMap[String(item.itemNumber)] = true;
								this.checkTemplate(String(item.itemNumber), item.templateRef);
							}
						}
					}
					this.isLoading = false;
					this.isOrderReady = true;
				},
				error: (err: unknown) => {
					this.isLoading = false;
					this.isError = true;
					console.error('unable to load order', err);
					this.eorderWizardService.showError();
				}
			}
		);
	}

	isTemplateValid(itemNumber?: string): boolean {
		if (!itemNumber) {
			return false;
		}
		return this.templateValidityMap[itemNumber] ?? false;
	}

	checkTemplate(itemNumber: string, templateRef?: string): Subscription {
		return this.templateService.getTemplateDetails(Number(templateRef))
			.pipe(take(1))
			.subscribe({
				next: res => {
					this.templateValidityMap[itemNumber] = Boolean(res.templateNumber);
					this.isLoading = false;
				},
				error: () => {
					this.templateValidityMap[itemNumber] = false;
					this.isLoading = false;
				}
			});
	}

	loadBocsForOrder(order: RequestOrder) {
		return this.eorderApiService.getBocs()
			.pipe(switchMap((userBocData: SoldToRelationshipGroup[]) => {
				if (userBocData.length === 0) {
					return of({ order, results: <BocClient[]>[] });
				}
				const soldToGroup = userBocData.find(v => v.soldTo === order.soldToRef);
				const businessIds = soldToGroup ? Array.from(
					new Set([soldToGroup.soldTo, ...soldToGroup.shipTos])
				) : [];
				return this.userService.getBocs(
					['businessId', 'name', 'city'],
					businessIds.map(x => `businessId:${ x }`).join(' OR ')
				).pipe(map(v => ({ order, results: v.results })));
			}));
	}

	ngOnDestroy() {
		this.orderSubscription.unsubscribe();
	}

	goBack() {
		this.locationService.back();
	}

	getRddForWeek(rdd?: string) {
		return rdd || UNDERSCORE;
	}

	getVariableFieldLabel(fieldLabel: string) {
		return this.productCharacteristicsTranslationService
			.translate(`${ NAMESPACE_A2C_FIELD }${ fieldLabel }`);
	}

	getShipToList(soldTo?: string) {
		const soldToGroup = soldTo && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldTo)
			: undefined;
		const { bocs } = this;
		return soldToGroup && bocs
			? soldToGroup.shipTos.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
	}

	getCustomerDetail(code?: string) {
		let customerDetail = '';
		if (code !== '' && code) {
			const formatedCode = code.replace(/^0+/, '');
			const selectedList = this.shipToList.filter(v => v.businessId === formatedCode);
			for (const listName of selectedList) {
				customerDetail = `${ listName.name } / ${ listName.city }`;
			}
		}
		return customerDetail;
	}

	getDescriptionByCode(code?: string, varType?: string) {
		let descriptionByCode = '';
		let bocList: CodeValue[] = [];
		if (varType === 'TMODE' && this.tmodeList && this.tmodeList.length > 0) {
			bocList = this.tmodeList;
		} else if (varType === 'SERVICELEVEL' && this.serviceLevelList && this.serviceLevelList.length > 0) {
			bocList = this.serviceLevelList;
		}
		if (code !== '') {
			const selectedVar = bocList.filter(field => field.code === code);
			for (const varField of selectedVar) {
				descriptionByCode = varField.description || '';
			}
		}
		return descriptionByCode;
	}

	async evtEditEordrDraft(id: number | undefined) {
		this.isLoading = true;
		if (id) {
			await this.eorderWizardService.actionEditEordrDraft(id);
		}
		this.isLoading = false;
	}

	getListOfProductData(listOfRequestProductData: RequestProductData[], type: string) {
		const variableList = new Map<string, string>();
		for (const productData of listOfRequestProductData) {
			if ((type === this.VARIABLE_ROW && !HIDDEN_VARIABLES.has(productData.characName)
				&& !FIRST_ROW_VARIABLES.has(productData.characName))
			|| (type === this.FIRST_ROW && FIRST_ROW_VARIABLES.has(productData.characName))) {
				if (productData.characValueTable && productData.characterDescription) {
					variableList.set(productData.characName, productData.characterDescription);
				} else {
					variableList.set(productData.characName, productData.characValueNum
						? productData.characValueNum.toString()
						: UNDERSCORE);
				}
			}
		}
		return variableList;
	}

	getExpandPanelStatus(index: number) {
		return this.isExpanded[index];
	}

	expandPanel(index: number) {
		this.isExpanded[index] = !this.isExpanded[index];
	}

	getTransportDescription(code: string): string {
		if (!code || !this.tmodeList?.length) {
			return '';
		}
		const match = this.tmodeList.find(x => x.code === code);
		return match ? match.description : UNDERSCORE;
	}

	getShiptoDesc(shipto: string): string {
		if (!shipto || !this.shipToList.length) {
			return '';
		}
		const trimmedId = shipto.replace(/^0+/, '') || '0';
		const match = this.shipToList.find(item => {
			const businessId = String(item.businessId).replace(/^0+/, '') || '0';
			return businessId === trimmedId;
		});

		return match ? `${ match.name } - ${ match.city }` : UNDERSCORE;
	}

	getProductData(
		list: RequestProductData[] | undefined,
		index: number
	): string | number {
		return list?.[index]?.characValueNum
			?? list?.[index]?.characterDescription
			?? UNDERSCORE;
	}

	isRddInPast(rddDateGet: string | undefined): boolean {
		if (!rddDateGet) {
			return false;
		}
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		let rddDate: Date | null = null;
		const weekMatch = /^W(\d{2})\/(\d{4})$/.exec(rddDateGet);
		if (weekMatch) {
			const week = Number(weekMatch[1]);
			const year = Number(weekMatch[2]);
			const monday = this.getMondayOfIsoWeek(week, year);
			rddDate = new Date(monday);
			rddDate.setDate(monday.getDate() + 6);
		} else if (/^(?:\d{2}\/){2}\d{4}$/.test(rddDateGet)) {
			const [day, month, year] = rddDateGet.split('/').map(Number);
			rddDate = new Date(year, month - 1, day);
		} else {
			const parsed = new Date(rddDateGet);
			if (!Number.isNaN(parsed.getTime())) {
				rddDate = parsed;
			}
		}
		if (!rddDate) {
			return false;
		}
		rddDate.setHours(0, 0, 0, 0);
		return rddDate < today;
	}

	isItemRddInvalid(orderItem: string | undefined): boolean {
		return this.isRddInPast(orderItem);
	}

	getXcarb(xcarbCode: string): string {
		const xcarbItem = this.xcarbList?.find(
			item => item.code === xcarbCode
		);
		return xcarbItem ? xcarbItem.description : UNDERSCORE;
	}

	getServiceLevel(serviceLevelCode: string | undefined): string {
		if (!serviceLevelCode) {
			return UNDERSCORE;
		}
		const serviceItem = this.serviceLevelList?.find(
			item => item.code === serviceLevelCode
		);
		return serviceItem ? serviceItem.description : UNDERSCORE;
	}

	private getMondayOfIsoWeek(week: number, year: number): Date {
		const jan4 = new Date(year, 0, 4);
		const jan4Day = jan4.getDay() || 7;
		const mondayWeek1 = new Date(jan4);
		mondayWeek1.setDate(jan4.getDate() - (jan4Day - 1));
		const result = new Date(mondayWeek1);
		result.setDate(mondayWeek1.getDate() + (week - 1) * 7);
		return result;
	}
}
